import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackLink from "../../components/navigation/BackLink/BackLink";

// ---------------------------------------------------------------------------
// The timetable workspace: ONE destination, with the seven surfaces as tabs.
//
// These were six separate cards on /admin and six top-level routes, which meant
// building a timetable cost eleven page loads — four of them pure round-trips
// back through the hub to reach the next step. They are one feature behind one
// permission code, so they are one destination.
//
// The shell owns everything the panels used to duplicate: the page wrapper, the
// back link, the WIDTH, and the permission guard (now enforced once on the
// layout route in App.js). Each panel therefore renders its own content and
// nothing else.
//
// Tab ORDER is by how often a surface is used once the school is set up, not by
// the order you first visit them. The setup sequence is taught by the empty
// states and the readiness banner, not by making the navigation a checklist —
// Lessons and Rules are edited every term and would be buried at the end.
//
// The draft board is deliberately NOT a tab: it is a full-shell takeover at
// /timetable/draft/:uuid, so the tab strip cannot be clicked away mid-edit.
// ---------------------------------------------------------------------------

const TABS = [
  // The live grid leads. It used to be Generate, labelled "Timetable" — so the
  // one thing a manager most often comes here to LOOK AT was not reachable at
  // all, and the tab that promised it showed a form instead.
  { to: "/timetable", end: true, key: "timetable.tab.overview" },
  { to: "/timetable/generate", key: "timetable.tab.generate" },
  { to: "/timetable/lessons", key: "timetable.tab.lessons" },
  { to: "/timetable/classes", key: "timetable.tab.classes" },
  { to: "/timetable/rooms", key: "timetable.tab.rooms" },
  { to: "/timetable/rules", key: "timetable.tab.rules" },
  { to: "/timetable/week", key: "timetable.tab.week" },
  { to: "/timetable/import", key: "timetable.tab.import" },
];

export default function TimetableWorkspace() {
  const { t } = useTranslation();

  return (
    <div className="min-h-full bg-slate-50 p-6">
      {/* ONE WIDTH, FOR EVERY TAB.
          It used to be per-tab — `max-w-[1600px]` for the grid and `max-w-6xl`
          for the rest — and three panels then capped themselves AGAIN on top of
          that (896px, 896px, 768px) with no `mx-auto`, so they hugged the left
          of a centred column. Four widths, two alignments. And because this box
          also holds the back link and the tab strip, switching tabs resized and
          re-centred the navigation itself: the strip moved out from under the
          cursor that had just clicked it.

          `max-w-7xl` is not a new number — it is exactly the container
          BaseLayout gives its own header bar, so the tab strip now lines up
          with the "Timetable" title above it, and it is what the app's other
          full-page surfaces use (Users, the dashboards, Standards).

          The grid tab loses ~320px of a table that scrolls horizontally at ANY
          width: the whole-school summary is ~2150px intrinsic, so 1600px was
          never "the whole week on screen" either — it was 29 of 41 columns
          instead of 21, bought by making every other tab move. */}
      <div className="mx-auto max-w-7xl">
        <BackLink />

        {/* Sticky so the tabs stay reachable on the long CRUD panels.
            The scroller is the inner element, not the <nav>: the nav owns the
            rule and the backdrop so both span the full column even when the
            strip is scrolled, and `pt-1` gives the focus ring somewhere to be
            drawn instead of being clipped by `overflow-x-auto`.

            z-40 beats the live grid's sticky table headers (z-30 and below),
            which scroll UNDER this bar and used to paint straight over it, and
            still loses to the z-50 overlays — modals and the grid's hover card
            — which render later in the tree and win the tie. */}
        <nav
          aria-label={t("timetable.tabsAria")}
          className="sticky top-0 z-40 mb-6 mt-4 border-b border-slate-200 bg-slate-50/95 backdrop-blur"
        >
          <div className="-mx-1 overflow-x-auto px-1 pt-1">
            {/* No `-mb-px`. The usual trick — pull the tab row down a pixel so
                its 2px underline lands ON the container's hairline instead of
                stacking above it — cannot work inside an `overflow-x-auto`
                box: overflow clips at the PADDING box, so that pixel is cut
                off and the active tab renders a 1px underline instead of 2px
                (measured). The 2px indigo simply sits on the slate hairline. */}
            <ul className="flex min-w-max items-center gap-1">
              {TABS.map((tab) => (
                <li key={tab.to}>
                  <NavLink
                    to={tab.to}
                    end={tab.end}
                    className={({ isActive }) =>
                      // `ring-inset`, not `ring-offset-2`: an offset ring is
                      // drawn OUTSIDE the element and this row is an
                      // `overflow-x-auto` box, which clips it — so keyboard
                      // focus on the app's primary navigation was showing a
                      // ring with its edges cut off. An inset ring cannot be
                      // clipped because it never leaves the element.
                      `block whitespace-nowrap rounded-t-lg border-b-2 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500 ${
                        isActive
                          ? "border-indigo-600 text-indigo-700"
                          : // slate-400 on slate-50 is ~2.9:1 — under the 4.5:1
                            // small-text minimum, on the app's primary
                            // navigation. slate-500 clears it.
                            "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900"
                      }`
                    }
                  >
                    {t(tab.key)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <Outlet />
      </div>
    </div>
  );
}
